import { X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useCreateCustomRiskRequest } from "@/api/safety/custom-risk-request";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogClose, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";
import { error as toastError, success as toastSuccess } from "@/providers/ToastProvider";
import { AboutStep } from "./AboutStep";
import { ConfigurationStep } from "./ConfigurationStep";
import { ExamplesStep } from "./ExamplesStep";
import { ModalStepper } from "./ModalStepper";
import { NotesStep } from "./NotesStep";

export type CustomRiskRequestForm = {
  about: { name: string; detect: string };
  examples: { positive: string; counter?: string };
  configuration: {
    severity?: "low" | "medium" | "high" | "critical";
    responsePolicy?:
      | "remove_from_gmv_max_only"
      | "send_warning_to_creator"
      | "send_warning_to_creator_and_remove_from_gmv_max"
      | "request_metadata_edit"
      | "request_video_removal"
      | "end_linked_collaboration"
      | "end_creator_relationship";
    reviewMode?: "auto_approve" | "require_review";
  };
  notes: { extra?: string };
};

const INITIAL_FORM: CustomRiskRequestForm = {
  about: { name: "", detect: "" },
  examples: { positive: "", counter: undefined },
  configuration: {},
  notes: {},
};

const STEP_LABELS = ["About", "Examples", "Configuration", "Notes"] as const;
type StepIndex = 0 | 1 | 2 | 3;

function buildSteps(currentStep: StepIndex) {
  return STEP_LABELS.map((label, i) => ({
    title: label,
    status: (i < currentStep ? "complete" : i === currentStep ? "current" : "incomplete") as
      | "complete"
      | "current"
      | "incomplete",
  }));
}

function validateStep(step: StepIndex, form: CustomRiskRequestForm): Record<string, string> {
  const errors: Record<string, string> = {};
  if (step === 0) {
    if (!form.about.name.trim()) errors["about.name"] = "Risk name is required";
    if (!form.about.detect.trim()) errors["about.detect"] = "Detection description is required";
  }
  if (step === 1) {
    if (!form.examples.positive.trim()) errors["examples.positive"] = "At least one example is required";
  }
  return errors;
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function RequestCustomRiskModal({ open, onOpenChange }: Props) {
  const [currentStep, setCurrentStep] = useState<StepIndex>(0);
  const [form, setForm] = useState<CustomRiskRequestForm>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { mutateAsync, isPending } = useCreateCustomRiskRequest();

  const steps = useMemo(() => buildSteps(currentStep), [currentStep]);

  const handleChange = useCallback((patch: Partial<CustomRiskRequestForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(patch)) {
        delete next[key];
        for (const k of Object.keys(next)) {
          if (k.startsWith(key)) delete next[k];
        }
      }
      return next;
    });
  }, []);

  const handleNext = useCallback(() => {
    const stepErrors = validateStep(currentStep, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep((prev) => Math.min(prev + 1, 3) as StepIndex);
  }, [currentStep, form]);

  const handlePrevious = useCallback(() => {
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 0) as StepIndex);
  }, []);

  const handleSubmit = useCallback(async () => {
    const stepErrors = validateStep(3, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    try {
      await mutateAsync({
        about: form.about,
        examples: form.examples,
        configuration: form.configuration,
        notes: form.notes,
      });
      toastSuccess("Request sent — we'll be in touch within 5–10 business days.");
      onOpenChange(false);
      setForm(INITIAL_FORM);
      setCurrentStep(0);
      setErrors({});
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }, [form, mutateAsync, onOpenChange]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setForm(INITIAL_FORM);
        setCurrentStep(0);
        setErrors({});
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* [&>button]:hidden suppresses the built-in close button rendered by ScrollableDialogContent */}
      <ScrollableDialogContent className="w-[750px] max-w-[750px] [&>button]:hidden">
        <div className="-mx-6 px-6 border-b border-secondary pb-6 relative flex gap-5">
          <div className="w-[90%]">
            <DialogTitle className="text-lg font-bold text-[#181D27] pr-10">Request a custom risk</DialogTitle>
            <p className="mt-1 text-[13px] text-tertiary">
              Tell us what you'd like to detect. We'll build, test, and configure it for you. Typical turnaround: 5 to
              10 business days.
            </p>
          </div>
          <DialogClose className="flex-1 text-gray-400 hover:opacity-75 transition-opacity focus:outline-none">
            <X className="size-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>

        <div className="-mx-6 px-6 border-b border-secondary py-5">
          <ModalStepper steps={steps} />
        </div>

        <div className="-mx-6 px-6 min-h-[300px] border-b border-secondary py-5">
          {currentStep === 0 && <AboutStep form={form} onChange={handleChange} errors={errors} />}
          {currentStep === 1 && <ExamplesStep form={form} onChange={handleChange} errors={errors} />}
          {currentStep === 2 && <ConfigurationStep form={form} onChange={handleChange} />}
          {currentStep === 3 && <NotesStep form={form} onChange={handleChange} />}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button color="secondary" size="md" onClick={handlePrevious} isDisabled={currentStep === 0}>
            Previous
          </Button>

          <div className="flex items-center gap-2">
            <Button color="secondary" size="md" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            {currentStep < 3 ? (
              <Button color="primary" size="md" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button color="primary" size="md" onClick={handleSubmit} isLoading={isPending}>
                Submit request
              </Button>
            )}
          </div>
        </div>
      </ScrollableDialogContent>
    </Dialog>
  );
}
